<template>
  <div ref="listWrapper" :class="['file-list', !items.length && 'empty']" @scroll.passive="onScroll">
    <ul>
      <!-- 搜索结果不同页之间可能重复 这里用下标做key, 用下标作为key 同时上传文件，新文件会获取到原来key的props，导致显示错误 -->
      <li v-for="(item, index) of items" :key="`${item.id}_${scenario === 'search' ? index : ''}`">
        <frequent-item
          v-if="isFrequent && scenario !== 'search'"
          :item="item"
          @item-click="onItemClick"
          @menu-click="onMenuClick"
          :config="config"
        />
        <upload-item
          v-else-if="item.is_upload"
          :item="item"
          @item-uploaded="onItemUploaded"
          @item-delete="onItemDeleted"
        />
        <file-item
          v-else
          :item="item"
          :hideAction="scenario === 'fileSelect' || scenario === 'share' && !isExternalShare"
          :scenario="scenario"
          :disableClickStyle="disableClickStyle || !item.action_permissions.includes('perm_download')"
          @item-click="onItemClick"
          @menu-click="onMenuClick"
          :config="config"
        />
      </li>
    </ul>
    <div>
    <guide-zone v-if="isRootDir && ua.wxwork && config.enterpirse_wxwork_guide_upload && !(listError || spaceClosed)" @upload-click='uploadAction'/>
    </div>
    <div class="loading-indicator" v-show="showLoading && items.length">
      <i class="circle-icon-loading"/>
      <span>{{$t('base.loading')}}</span>
    </div>
    <div class="empty-state" v-show="!items.length">
      <van-loading v-if="isFetching" color="black" />
      <template v-else-if="isFrequent">
        <svg-icon icon="empty-frequent" />
        <p>{{$t('file.frequent.empty')}}</p>
        <p class="sub">{{$t('file.frequent.empty.sub')}}</p>
      </template>
      <div class="search-empty" v-else-if="scenario === 'search'">
        <svg-icon icon="empty-search" />
        <p>{{$t('file.search.empty')}}</p>
      </div>
      <div v-else-if="spaceClosed || listError" class="error-state">
        <svg-icon v-if="spaceClosed" icon="empty-nopermission" />
        <i v-else class="iconfont icon-prompt" />
        <p v-if="spaceClosed">{{$t('file.own.closed')}}</p>
        <p :class="{'enterprise-expire': enterpriseExpire}" v-else>{{(enterpriseExpire && $t('base.expire')) || $t('file.removed')}}</p>
        <a v-if="listError && !enterpriseExpire" href="/" class="btn">{{$t('base.back.home')}}</a>
      </div>
      <template v-else>
        <svg-icon icon="empty-default" />
        <p>{{$t('file.list.empty')}}</p>
      </template>
    </div>
  </div>
</template>
<script>
import debounce from 'lodash/debounce'
import ajax from '@/utils/ajax'
import {i18n} from '@/utils/i18n'
import {normalizeFile, normalizeUploadFile} from '@/utils/fileUtils'
import {toast, updateUserGuideSetting, ua, guid} from '@/utils/common'
import {FILE_LIST, FREQUENT_LIST, FILE_LIST_COLLAB, EXTERNAL_ENTERPRISE_FILES, TRASH_LIST, FILE_SEARCH} from '@/config/api'
import FrequentItem from './FrequentItem'
import UploadItem from './UploadItem'
import FileItem from './FileItem'
import config from '@/config'
import GuideZone from '../utils/GuideZone'

const DEFAULT_LIST_LIMIT = 25
let oldListInfo = {}

const spaceNames = {
  own: i18n.t('file.personalFiles'),
  collab: i18n.t('file.collabWithMe')
}

export default {
  name: 'FileList',
  i18n,
  components: {
    FrequentItem,
    UploadItem,
    GuideZone,
    FileItem
  },
  props: {
    trashFilterSelected: String | Number,
    uploadFiles: Object,
    selectedFiles: {
      type: Array,
      default: () => []
    },
    scenario: {
      type: String,
      default: 'fileView'
    },
    searchInFolder: Boolean,
    spaceClosed: Boolean,
    isExternalShare: Boolean,
    permSave: Boolean,
    isMoreShare: Boolean,
    uniqueName: String,
    process: Object,
    fileListData: {
      type: Array,
      default: () => []
    }
  },
  data () {
    const { params } = this.$route
    return {
      listOffset: 0,
      isFetching: false,
      isFrequent: false,
      items: [],
      searchText: this.$route.query.search_text || '',
      showLoading: false,
      listError: false,
      disableClickStyle: this.selectedFiles.length >= 10,
      enterpriseExpire: false,
      isRootDir: params.space === 'own',
      ua,
      config
    }
  },
  watch: {
    $route (to, from) {
      /* eslint-disable eqeqeq */
      const spaceChange = from.params.space !== to.params.space
      const idChange = from.params.id != to.params.id || from.query.folder_id !== to.query.folder_id

      const newSearchText = to.query.search_text ? to.query.search_text.trim() : ''
      const oldSearchText = from.query.search_text ? from.query.search_text.trim() : ''
      const searchTextChange = this.scenario === 'search' && newSearchText !== oldSearchText

      this.isRootDir = to.params.space === 'own'
      // 不同space 或 id 清空列表
      if (spaceChange || idChange || searchTextChange) {
        this.items = []
      }
      if (searchTextChange) {
        this.searchText = newSearchText
        this.isFetching = true
        this.debouncedFetchList()
      } else if (spaceChange || idChange) {
        this.fetchList()
      }
      /* eslint-enable eqeqeq */
    },
    'selectListStack' (stack) {
      if (this.scenario === 'fileSelect' && stack.length) {
        this.fetchList()
      }
    },
    'store.sortInfo' (val, oldVal) {
      if (oldVal) { // 上一个值不为空(第一次请求完列表 会设置store里面的sortInfo 这时候不需要重新请求)
        this.listOffset = 0
        this.fetchList(true)
      }
    },
    'store.newFolder' (item, oldItem) {
      if (item.id === oldItem.id) {
        return
      }
      if (item.spaceInfo && this.scenario !== 'search') {
        // 文件选择器上新建文件夹或复制item 目标位置与底层文件列表目录一致 则插入新 item 到文件列表中
        if (this.scenario === 'fileSelect' || (this.$route.params.space === item.spaceInfo.space && this.$route.params.id - 0 === item.spaceInfo.id - 0)) {
          this.items.unshift(item)
        }
      } else {
        // 文件列表正常插入
        this.items.unshift(item)
      }
    },
    'store.actionState' (state) {
      const { action, type, id, name, is_frequently_used } = state
      const typed_id = `${type}_${id}`

      switch (action) {
        case 'rename':
          this.onItemRenamed(id, name)
          break

        case 'delete':
          this.onItemDeleted(typed_id)
          break

        case 'frequent':
          this.onItemFrequentChange(typed_id, is_frequently_used)
          if (this.isFrequent && !is_frequently_used) {
            this.onItemDeleted(typed_id)
          }
          break

        default:
          break
      }
    },
    uploadFiles (files, oldFiles) {
      if (files === oldFiles) return
      const uploadItems = {}
      const versionItems = {}
      const oldItems = {}
      const uuid = guid()
      for (let id in files) {
        const item = files[id]
        item.uuid = uuid
        normalizeUploadFile(item, id)
        if (item.file_id) {
          const fileItem = this.items.filter(({typed_id}) => item.type_id === typed_id)[0]
          if (fileItem) {
            if (!oldItems[item.type_id]) oldItems[item.type_id] = fileItem
            versionItems[item.typed_id] = item
          } else {
            uploadItems[item.typed_id] = item
          }
        } else {
          uploadItems[item.typed_id] = item
        }
      }
      this.items.unshift(...Object.keys(uploadItems).map(id => uploadItems[id]))
    },
    searchInFolder (value, oldVal) {
      if (value !== oldVal) {
        this.items = []
        this.fetchList()
      }
    },
    selectedFiles (newFiles, oldFiles) {
      if (oldFiles.length > newFiles.length) {
        this.items.map(item => {
          if (item.type === 'file') {
            item.selected = newFiles.findIndex(file => file.id === item.id) > -1
          }
          return item
        })
      }
      this.disableClickStyle = config.openConfig.selectFileMax && newFiles.length >= config.openConfig.selectFileMax
    },
    items (arr) {
      // 准备好图片预览需要的信息
      this.store.imgPreviewInfo.ids = []
      this.store.imgPreviewInfo.items = {}

      arr.forEach(item => {
        if (item.extension_category === 'image') {
          this.store.imgPreviewInfo.ids.push(item.id)
          this.store.imgPreviewInfo.items[item.id] = item
        }
      })
    }
  },
  created () {
    this.fetchList()
    this.debouncedFetchList = debounce(this.fetchList, 1000)
  },
  methods: {
    fetchList (scrollToTop) {
      this.isFetching = true
      if (this.isMoreShare) {
        let fileList = this.fileListData && this.fileListData.length && this.fileListData.map(item => {
          this.normalizeItem(item, 'share')
          return item
        })
        this.items = fileList
        return
      }
      const params = this.selectListPeek
      const {space, id, module} = params || this.$route.params
      const {search_text, search_in, folder_id, unique_name} = this.$route.query
      const [sort_by, sort_direction] = this.store.sortInfo.split('_')

      const newListInfo = {space, id, search_text, search_in, folder_id}
      for (const key in newListInfo) {
        if (newListInfo[key] !== oldListInfo[key]) {
          this.listOffset = 0
          break
        }
      }

      const query = {}
      this.requestQuery = {}
      query.limit = DEFAULT_LIST_LIMIT
      query.offset = this.listOffset

      let url = FILE_LIST

      if (this.scenario !== 'search') {
        if (space !== 'trash') { // 搜索和回收站有自己的排序方式
          if (sort_by) {
            query.sort_by = sort_by
          }
          if (sort_direction) {
            query.sort_direction = sort_direction
          }
        }

        // wap分享
        if (this.isExternalShare) {
          if (!folder_id) {
            return
          }
          url += '/' + folder_id
          query.scenario = 'share'
          query.sort_by = sort_by || 'name'
          query.sort_direction = sort_direction || 'asc'
          query.share_token = this.uniqueName
        } else if (space === 'frequent') {
          url = FREQUENT_LIST + '?page_size=100'
          this.requestQuery.typeId = 'frequent_0'
        } else if (space === 'folder') {
          url += `/${id}`
          if (this.scenario === 'share') {
            query.scenario = 'share'
            query.share_token = unique_name
          }
          this.requestQuery.typeId = `folder_${id}`
        } else if (space === 'dept') {
          url += '/0'
          query.department_id = id
          this.requestQuery.typeId = `folder_${id}`
        } else if (space === 'own') {
          url += '/0'
          query.folder_type = 'own'
          this.requestQuery.typeId = 'own_0'
        } else if (space === 'collab') {
          url = FILE_LIST_COLLAB
          this.requestQuery.typeId = 'collab_0'
        } else if (space === 'external') {
          const [extType, extId] = id.split('_')
          url = `${EXTERNAL_ENTERPRISE_FILES}`
          query[`${extType}_id`] = extId
          this.requestQuery.typeId = `external_${extId}`
        } else if (space === 'trash') {
          url = TRASH_LIST

          this.requestQuery.typeId = 'trash_0'
          if (id && id !== 'my_0') {
            url += `?external_typed_id=${id}`
            this.requestQuery.typeId = `trash_${id}`
          }
        }
      } else {
        url = FILE_SEARCH
        if (!this.searchText || !this.searchText.trim()) {
          this.isFetching = false
          return
        } else {
          // ios 中文输入会带一些转义之后是 %E2%80%86 的特殊字符
          /* eslint-disable no-irregular-whitespace */
          const text = this.searchText.replace(/ /g, '')
          /* eslint-enable no-irregular-whitespace */

          query.query_words = text
          this.requestQuery.query_words = text
          // query.record_search_history = true
        }
        if (this.searchInFolder) {
          const { space, id } = this.$route.params
          switch (space) {
            case 'own':
              query.department_id = 0
              this.requestQuery.department_id = '0'
              break

            case 'collab':
              query.department_id = -1
              this.requestQuery.department_id = '-1'
              break

            case 'dept':
              if (id) {
                query.department_id = id
                this.requestQuery.searchIn = `department_${id}`
              }
              break

            case 'folder':
              if (id) {
                query.search_in_folder = id
                this.requestQuery.searchIn = `folder_${id}`
              }
              break

            case 'external':
              if (id) {
                const [type, externalId] = id.split('_')
                if (id) {
                  query[`${type}_id`] = externalId
                  this.requestQuery.searchIn = id
                }
              }
              break

            default:
              break
          }
        }
      }

      // 文件选择模式下只有文件夹
      if (this.scenario === 'fileSelect' && (space === 'folder' || space === 'own')) {
        query.folder_type = 'folder'
      } else if (module === 'select_destination') {
        if (this.scenario === 'search') {
          query.file_type_category = 'folder'
        } else {
          query.folder_type = 'folder'
        }
      }

      if (space !== 'frequent' || this.scenario === 'search') {
        let queryString = ''
        for (let key in query) {
          queryString += `&${key}=${encodeURIComponent(query[key])}`
        }
        if (queryString) {
          if (/\?/.test(url)) {
            url += queryString
          } else {
            url += '?' + queryString.substr(1)
          }
        }
      }

      ajax({ url, requestQuery: this.requestQuery }).then(result => {
        this.listError = !!result.errors && this.$route.name !== 'OpenView'
        const {errors: {enterprise_expired} = {}} = result
        // 套餐到期, 通过enterprise_expired 字段来判断
        if (enterprise_expired) this.enterpriseExpire = true
        // if (this.scenario === 'search') { // 切换当前文件夹和全部文件过快
        //   const searchInOfResult = result.search_in_folder || result.search_in_department || result.search_in_enterprise || result.search_in_user
        //   if (
        //     this.requestQuery.query_words !== result.query_words || // 判断搜索内容是否相同
        //     (this.requestQuery.department_id && this.requestQuery.department_id !== result.department_id) || // 个人空间和与我协作下搜索当前文件夹
        //     (this.requestQuery.searchIn && !searchInOfResult) || // 其他情况下搜索当前文件夹
        //     (searchInOfResult && this.requestQuery.searchIn !== `${searchInOfResult.type}_${searchInOfResult.id}`)
        //   ) {
        //     this.isFetching = false
        //     return
        //   }
        // }
        if (JSON.stringify(this.requestQuery) !== JSON.stringify(result.requestQuery)) {
          return
        }
        this.totalCount = result.total_count || result.total_children_count || result.num_found || result.num_found
        this.isFrequent = space === 'frequent'
        // 常驻底部的loading
        this.showLoading = !this.isFrequent && this.listOffset + DEFAULT_LIST_LIMIT < this.totalCount

        const files = result.children || result.frequent_used_items || result.items || []

        const items = files.map(item => {
          if (space === 'frequent' && this.scenario !== 'search') {
            const { item_type } = item
            item = {
              ...item,
              item: item.item || item[item_type] || {}
            }
          } else if (space !== 'trash' && item.type === 'file' && this.selectedFiles.length) {
            if (this.selectedFiles.findIndex(file => file.id === item.id) > -1) item.selected = true
          } else if (space === 'collab') {
            item = {
              ...item,
              isTopCollabFolder: true
            }
          }
          this.normalizeItem(item, space)
          return item
        })

        if (this.listOffset === 0) {
          this.items = items
        } else {
          this.items = this.items.concat(items)
        }

        if (scrollToTop) { // 切换排序后主动回滚到顶部
          this.$refs.listWrapper.scrollTop = 0
        }

        this.isFetching = false
        oldListInfo = newListInfo

        // 搜索结果页 和 回收站 不记录
        if (result.sort_by && result.sort_direction && this.scenario !== 'search' && space !== 'trash' && this.store.sortInfo !== `${result.sort_by}_${result.sort_direction}`) {
          this.setState({sortInfo: `${result.sort_by}_${result.sort_direction}`})
        }

        if (this.listOffset === 0) {
          const listInfo = {
            space,
            permissions: result.permissions,
            is_frequently_used: result.is_frequently_used,
            current_path: result.current_path,
            error: this.listError
          }
          this.$emit('list-info-change', listInfo)
        }
      })
    },
    normalizeItem (item, space) {
      if (space !== 'frequent' || this.scenario === 'search') {
        if (this.scenario === 'share' || this.isExternalShare) {
          item.from_share = true
        }
        normalizeFile(item)
        return
      }

      // frequent
      const { item_type, item_id } = item

      item.item = {
        ...item.item,
        typed_id: `${item_type}_${item_id}`,
        is_frequently_used: true,
        name: item.item.name || spaceNames[item_type] || ''
      }

      if (['folder', 'file'].includes(item_type)) {
        item.freq_type = 'file'

        const { parent, path } = item.item
        if (parent) {
          if (parent.space) {
            item.item.space = parent.space
          } else if (parent.type === 'enterprise' || parent.type === 'user') {
            item.item.space = 'external_space'
            if (path) path.unshift(parent)
          }
        }
        normalizeFile(item.item)
      } else {
        item.freq_type = 'space'

        if (!item.item.name && item_type === 'department') {
          item.item.name = i18n.t('frequent.list.departementDeleted')
          item.item.children_departments_count = 0
          item.item.is_forbidden = true
        }

        if (item.item.is_dismissed) {
          item.item.name += i18n.t('frequent.list.dismissed')
        }

        item.item_count = item.item.item_count || item.item.folder_count || 0
      }

      item.isForbidden = this.checkForbidden(item.item)
    },
    checkForbidden (item) {
      const { is_forbidden, is_dismissed, permissions } = item
      return is_forbidden || is_dismissed || (permissions && !permissions.includes('item_view'))
    },
    onItemClick (item) {
      let space, id, spaceType
      if ((item.freq_type && item.freq_type === 'space') || item.type === 'space') {
        spaceType = item.item_type || item.space
        if (spaceType === 'own' || spaceType === 'collab') {
          space = spaceType
        } else if (spaceType === 'department') {
          space = 'dept'
          id = item.item_id || item.id
        } else if (spaceType === 'enterprise' || spaceType === 'user') {
          space = 'external'
          id = `${spaceType}_${item.item_id || item.id}`
        }
      } else if (item.type === 'folder' || item.item_type) {
        space = 'folder'
        id = item.id
      }
      if (item.type === 'file') {
        if (this.$route.name === 'OpenView') {
          if (!((this.disableClickStyle || !item.action_permissions.includes('perm_download')) && !item.selected)) {
            item.selected = !item.selected
            this.$emit('item-select-toggle', item)
          }
          return
        }

        if (item.action_permissions && item.action_permissions.includes('perm_preview')) {
          let params = { file_id: item ? item.id : undefined }
          if (this.isMoreShare) {
            params.is_more_share = 1
            params.enterprise_id = item.enterprise_id
          }
          if (item && item.extension_category && this.isExternalShare) {
            params.share_extension_category = item.extension_category
          }
          this.toggleQuery('preview', params)
        } else {
          toast(i18n.t('preview.noPermission'))
        }
      } else {
        if (this.scenario === 'fileSelect') {
          this.selectListStack.push({space, id})
        } else {
          const { sort_by, sort_direction, unique_name } = this.$route.query
          const extraParams = {}
          if (unique_name) {
            extraParams.unique_name = unique_name
          }
          if (this.isExternalShare) {
            this.$router.push({query: {scenario: 'share', folder_id: id, ...extraParams}})
          } else {
            this.$router.push({params: {module: this.$route.params.module, space, id}, query: {sort_by, sort_direction, scenario: this.scenario === 'share' ? 'share' : undefined, ...extraParams}})
          }
        }
      }
    },
    onScroll: debounce(function (e) {
      if (!this.isFrequent && !this.isFetching && e.target.scrollTop + e.target.clientHeight > e.target.scrollHeight - 100) {
        this.loadMore()
      }
    }, 500),
    loadMore () {
      if (this.listOffset + DEFAULT_LIST_LIMIT < this.totalCount) {
        this.listOffset = this.listOffset + DEFAULT_LIST_LIMIT
        this.fetchList()
      }
    },
    onMenuClick (actionItem, from) {
      this.setState({actionSheet: {
        actionItem: actionItem,
        actionFrom: from,
        permSave: (this.permSave) || (this.isMoreShare && actionItem && actionItem.can_save)
      }})
    },
    onItemRenamed (id, name) {
      const itemInList = this.items.find(item => {
        // eslint-disable-next-line eqeqeq
        if (item.id == id || (this.isFrequent && item.item.id == id)) {
          return true
        }
      })

      if (itemInList) {
        if (this.isFrequent) {
          Object.assign(itemInList.item, {name})
        } else {
          Object.assign(itemInList, {name})
        }
      }
    },
    onItemDeleted (typed_id) {
      this.items = this.items.filter(item => {
        // eslint-disable-next-line eqeqeq
        if ((!this.isFrequent && item.typed_id != typed_id) || (this.isFrequent && item.item.typed_id != typed_id)) {
          return true
        }
      })
    },
    onItemFrequentChange (typed_id, is_frequently_used) {
      let itemInList = this.items.find(item => {
        if (item.typed_id === typed_id || (this.isFrequent && item.item.typed_id === typed_id)) {
          return true
        }
      })
      if (itemInList) {
        if (this.isFrequent) {
          Object.assign(itemInList.item, {is_frequently_used})
          normalizeFile(itemInList.item)
        } else {
          Object.assign(itemInList, {is_frequently_used})
          normalizeFile(itemInList)
        }
      }
    },
    onItemUploaded (uploadedItem, upload_id) {
      // 企业微信引导， 如果是企业为第三方应用过来的用户，第一次成功上传文件向后台发送一个取消引导的标记位
      if (config.enterpirse_wxwork_guide_upload) {
        new Promise((resolve) => {
          return resolve(updateUserGuideSetting({user_guide_h5_upload: 1}))
        }).then(() => {
          config.enterpirse_wxwork_guide_upload = false
        })
      }
      this.items = this.items.map(item => {
        if (item.typed_id === upload_id) {
          return uploadedItem
        }
        return item
      })
    },
    uploadAction () {
      this.$emit('upload-click')
    }
  }
}
</script>

<style lang="postcss">
@import 'variables';

.file-list {
  overflow: auto;
  // -webkit-overflow-scrolling: touch;

  & li {
    &:last-of-type {

      & .has-border-bottom {
        border: 0
      }
    }
  }

  & .file-item {
    position: relative;
    display: flex;
    min-height: 60px;
    background-color: #fff;
  }

  & .mask {
    z-index: 1;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 1px;
    left: 0;
    background-color: rgba(255, 255, 255, .8);
  }

  & .file-item.clickable:active {
    background: #eee;
  }

  & .space-item {

    & .item-main {
      display: flex;
      align-items: center;

        & .item-name {
          margin: 0;
        }
    }
  }

  & .thumb-icon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 auto;
    width: 55px;
    height: 60px;

    & .type-icon {
      width: 30px;
      height: 30px;
    }

    & img {
      width: 30px
    }
  }

  & .item-main {
    flex: 1 1 auto;
    overflow: hidden;
    box-sizing: border-box;
    min-height: 60px;
    padding: 10px 10px 10px 0;
    text-align: left;

    & .item-name {
      font-size: 16px;
      line-height: 22px;
      margin-bottom: 3px;

      & .name {
        flex: 1 1 auto;
        width: 0;
      }

      & .loaded {
        color: var(--secondlyText);
        flex: 0 0 auto;
        font-size: 12px;
      }
    }

    & .item-meta {
      color: var(--secondlyText);
      font-size: 12px;
      line-height: 16px;

      & .icon-file-encrypt {
        color: var(--contentIcon);
        font-size: 12px;
        margin-right: 3px;
        /* vertical-align: -2px; */
      }
    }

    & .search-result {
      font-size: 14px;
      color: #4b5255;

      & .content {
        margin-top: 10px;
      }
    }

    & .match-content {
      line-height: 20px;
      max-height: 60px;

      &.content {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        /*! autoprefixer: off */
        -webkit-box-orient: vertical;
      }
    }

    & .tag-content {
      line-height: 18px;
      margin-top: 8px;
    }

    & .collab-path-content {
      color: var(--secondlyText);
      font-size: 12px;
      line-height: 16px;
      margin-top: 10px;
    }

    & .tag {
      display: inline-block;
      vertical-align: middle;
      box-sizing: border-box;
      padding: 0 6px;
      margin-right: 6px;
      max-width: 100px;
      border-radius: 2px;
      border: 1px solid #d1d4df;
      color: #4e5660;
      font-size: 12px;
    }

    & .tag-num {
      display: inline-block;
      font-size: 12px;
    }

    & .hit-num {
      background-color: #fff9a9;
    }
  }

  & .upload-item {
    & .item-name {
      display: flex;
      margin-bottom: 0;
    }
  }

  & .item-action {
    z-index: 1;
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    width: 32px;
    padding-left: 0px;

    & .iconfont {
      color: var(--contentIcon);
      font-size: 22px;
    }

    & .icon-close-small {
      width: 12px;
      height: 12px;
      padding: 4px;
      border-radius: 50%;
      background-color: var(--contentIcon);
      color: #fff;
      font-size: 12px;
    }

    & .icon-arrow-next {
      font-size: 18px;
    }
  }

  & .waiting-info {
    color: var(--secondlyText);
    font-size: 12px;
  }

  & .error-info {
    color: var(--dangerColor);
    font-size: 12px;

    & .type-icon {
      margin-right: 5px;
    }

  }

  & progress {
    border: 0;
    height: 2px;
    width: 100%;
    margin-bottom: 10px;
    background: var(--progressBarBackground);
    color: var(--progressBarBase);

    &::-webkit-progress-bar {
      background: var(--progressBarBackground);
    }

    &::-webkit-progress-value {
      background: var(--progressBarBase);
    }
    &::-moz-progress-bar {
      background: var(--progressBarBase);
    }
  }

  & .loading-indicator {
    color: #999;
    height: 50px;
    line-height: 40px;
    text-align: center;
    // margin-bottom: -50px;

    & .circle-icon-loading {
      display: inline-block;
      width: 14px;
      height: 14px;
      border: 2px solid #cacaca;
      border-bottom: 2px solid transparent;
      border-radius: 100%;
      animation: spin infinite 1s linear;
      vertical-align: middle;
    }

    & span {
      margin-left: 5px;
      vertical-align: middle;
    }
  }

  &.empty {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #c1c1c1;

    & .empty-state,
    & .error-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;

      & .enterprise-expire {
        width: 80%
      }
    }
  }

  & .search-empty {
    color:#848484
  }

  & .error-state {

    & i {
      font-size: 56px;
      color: var(--contentIcon)
    }
    & p {
      margin: 20px 0
    }

    & .btn {
      display: inline-block;
      min-width: 90px;
      height: 35px;
      line-height: 35px;
      border-radius: 2px;
      background-color: #fff;
      border: solid 1px #dbdcdd;
      text-align: center;
      font-size: 14px;
      color: #000
    }
  }

  & .sub {
    font-size: 12px
  }
}

/* animations */
@keyframes spin {
  0% {
    transform: rotateZ(0deg);
  }

  100% {
    transform: rotateZ(360deg);
  }
}
</style>
